import { PaginationProperties, TableDataProperties } from '../../../shared/uiComponents/Table/tableProperties';

import { InfoClientProperties } from './clientProperties';
import { SessionTypeProperties, UserBriefInfoProperties } from './calendarProperties';
import { ErrorProperties } from '../identityAPIProperties';
import { InsuranceAuthorizationTypeProperties } from './insuranceAuthorizationProperties';
import { MBHBillingStatus } from './billingProperties';

export interface SoapNotesPaginationProperties extends PaginationProperties {
	clientId?: string;
	endDate?: string;
	startDate?: string;
	userId?: string;
}

export interface GetSoapNotesProperties extends SoapNotesPaginationProperties {
	includeBtSessions?: boolean;
	sessionType?: string;
	status?: string;
}

export interface ApprovePendingSoapNotesProperties {
	reportIds: string[];
	signature: string;
}


export interface DSUSoapNoteProperties extends SoapNotesFullProperties {
	supervisedSessionStatus: StatusProperties;
}

export interface GetDSUSoapNotesProperties extends SoapNotesPaginationProperties {
	status?: string[];
	supervisedStatus?: string[];
}

export interface GetSoapNoteProperties {
	clientId: string;
	reportId: string;
}

export interface CreateSoapNoteProperties {
	clientId: string;
	sessionId: string;
}

export interface UnlockSoapNoteProperties extends GetSoapNoteProperties {
	nextExpirationDate: string;
}

export interface StatusProperties {
	name: string;
	status: number;
}

export interface LocationTypes {
	id: string;
	locationType: number;
	name: string;
	telehealth: boolean;
}

export interface TelehealthTypes {
	id: string;
	name: string;
	type: number;
}

export enum TelehealthLocationTypes {
	'Client' = 1,
	'User' = 2,
}

export type SoapNoteTypes =
	| BehaviorTreatmentProperties
	| DirectSupervisionProperties
	| AssessmentProperties
	| BehaviorTreatmentModificationProperties
	| ParentTrainingProperties
	| TreatmentPlanningProperties
	| MeetAndGreetProperties
	| DiscretionarySessionProperties
	| null;

export interface InitialSoapNoteStateProperties {
	assessmentOptions: AssessmentOptionsProperties;
	bcbaStatuses: string[];
	behaviorTreatmentModificationOptions: BehaviorTreatmentModificationOptionsProperties;
	behaviorTreatmentHardData: BehaviorTreatmentHardDataProperties | null;
	behaviorTreatmentOptions: BehaviorTreatmentOptionsProperties;
	btStatuses: string[];
	directSupervisionOptions: DirectSupervisionOptionsProperties;
	dsuSoapNotes: SoapNotesDataProperties;
	error: ErrorProperties;
	initialLoading: boolean;
	isUpdating: boolean;
	loading: boolean;
	locations: LocationTypes[] | null;
	meetAndGreetOptions: MeetAndGreetOptionsProperties;
	parentTrainingOptions: ParentTrainingOptionsProperties;
	pendingSoapNotes: PendingApprovalDataProperties;
	soapNote: SoapNoteTypes;
	soapNoteData: {
		supervisor: {
			authoritationType: InsuranceAuthorizationTypeProperties;
			fullName: string;
			id: string;
			role: string;
		} | null;
	};
	soapNotes: SoapNotesDataProperties;
	soapNotesDrafts: SoapNotesDataProperties;
	telehealtLocations: TelehealthTypes[];
	treatmentPlanningOptions: TreatmentPlanningOptionsProperties;
}

export interface SoapNotesFullProperties {
	client: InfoClientProperties;
	clientTelehealthLocation: LocationTypes;
	createdAt: string;
	createdBy: UserBriefInfoProperties | null;
	date: string;
	endTime: string;
	id: string;
	location: LocationTypes;
	mbhBillingStatus: MBHBillingStatus | null;
	reportType: string;
	sessionId: string;
	sessionType: SessionTypeProperties;
	signedAt?: string | null;
	startTime: string;
	status: StatusProperties;
	submittedAt: string | null;
	submittedBy: UserBriefInfoProperties | null;
	user: UserBriefInfoProperties;
	userTelehealthLocation: LocationTypes;
}

export interface SoapNotesBehaviorTreatmentFullProperties extends SoapNotesFullProperties {
	behaviorTechName: string;
	supervisor: UserBriefInfoProperties;
}

export interface SoapNotesDataProperties extends TableDataProperties {
	query: SoapNotesFullProperties[] | null;
}

export interface OptionProperties {
	id: string | number;
	text: string;
}

export interface ParticipantOptionsProperties {
	id: string;
	isDefault: boolean;
	text: string;
}

export interface UpdateBillingCredentialedProperties {
	clientIds: string[];
	startDate: string;
	userId: string;
}

export interface GetExcelReportProperties {
	clientId?: string | null;
	endDate?: string | null;
	startDate?: string | null;
	userId?: string | null;
}

export interface PendingApprovalNoteProperties {
	id: string;
	date: string;
	userFullName: string;
	clientFullName: string;
	isElc: boolean;
}

export interface PendingApprovalDataProperties extends TableDataProperties {
	query: PendingApprovalNoteProperties[] | null;
}

interface DataProperties {
	clientTelehealthLocationId: string;
	date: string;
	endTime: string;
	forcedOperation: boolean;
	locationId: string | null;
	sessionId: string;
	sessionSummary: string;
	signature: string;
	startTime: string;
	submit: boolean;
	userTelehealthLocationId: string;
}

interface IdDataProperties {
	clientId: string;
	reportId: string;
}

interface AssessmentDataProperties extends DataProperties {
	assessmentActivityOther: string;
	assessmentParticipantOther: string;
	dataCollected: string;
	goalsCreated: string;
}

export interface UpdateAssessmentProperties extends AssessmentDataProperties, IdDataProperties {
	assessmentActivityResponses: string[];
	assessmentToolResponses: string[];
	participantResponses: string[];
	treatmentPlanResponses: string[];
}

export interface AssessmentOptionsProperties {
	assessmentActivityOptions: OptionProperties[];
	assessmentParticipantOptions: ParticipantOptionsProperties[];
	assessmentToolOptions: OptionProperties[];
	treatmentPlanOptions: OptionProperties[];
}

export interface AssessmentProperties extends SoapNotesFullProperties, AssessmentDataProperties {
	assessmentActivityResponses: OptionProperties[];
	assessmentToolResponses: OptionProperties[];
	participantResponses: OptionProperties[];
	treatmentPlanResponses: OptionProperties[];
}

interface BehaviorTreatmentModificationDataProperties extends DataProperties {
	barrierToTreatmentOther: string;
	dataAnalyzed: string;
	goalsAddressed: string;
	maladaptiveBehaviorsObserved: string;
	responseToIntervention: number | null;
	treatmentPlanUpdates: string;
}

export interface UpdateBehaviorTreatmentModificationProperties
	extends BehaviorTreatmentModificationDataProperties, IdDataProperties {
	barrierToTreatmentResponses: string[];
	behaviorTreatmentModificationInterventionResponses: string[];
	behaviorTreatmentModificationParticipantOther: string;
	behaviorTreatmentModificationResponses: string[];
	participantResponses: string[];
	reinforcementResponses: string[];
	sessionId: string;
}

export interface BehaviorTreatmentModificationOptionsProperties {
	barrierToTreatmentOptions: OptionProperties[];
	behaviorTreatmentModificationInterventionOptions: OptionProperties[];
	behaviorTreatmentModificationParticipantOptions: ParticipantOptionsProperties[];
	reinforcementOptions: OptionProperties[];
	responseToInterventionOptions: OptionProperties[];
}

export interface BehaviorTreatmentModificationProperties
	extends SoapNotesFullProperties, BehaviorTreatmentModificationDataProperties {
	barrierToTreatmentResponses: OptionProperties[];
	behaviorTreatmentModificationInterventionResponses: OptionProperties[];
	participantResponses: OptionProperties[];
	reinforcementResponses: OptionProperties[];
}

interface BehaviorTreatmentDataProperties extends DataProperties {
	barrierToTreatmentOther: string;
	behaviorTreatmentFuturePlanOther: string;
	clientMoodOther: string;
	clientResponseToTreatment: number | null;
	frequencyAndDurationOfMaladaptiveBehaviors: string;
	howBarrierAffect: string;
	isAiGenerated: boolean;
	isSessionSupervised: boolean;
	levelOfMaladaptiveBehavior: number | null;
	maladaptiveBehaviors: string;
	notes: string;
	reinforcementOther: string;
	responseToIntervention: number | null;
	sessionDescription: string;
	supervisor: UserBriefInfoProperties;
	supervisorId: string;
	workedGoals: string;
}

export interface UpdateBehaviorTreatmentProperties extends BehaviorTreatmentDataProperties, IdDataProperties {
	barrierToTreatmentResponses: string[];
	behaviorTreatmentFuturePlanResponses: string[];
	behaviorTreatmentParticipantOther: string;
	behaviorTreatmentTechniquesResponses: string[];
	clientMoodResponses: string[];
	maladaptiveBehaviorTechniquesResponses: string[];
	participantResponses: string[];
	promptLevelsResponses: string[];
	reinforcementResponses: string[];
	skillDomainAddressedResponses: string[];
}

export interface BehaviorTreatmentProperties extends SoapNotesFullProperties, BehaviorTreatmentDataProperties {
	barrierToTreatmentResponses: OptionProperties[];
	behaviorTreatmentFuturePlanResponses: OptionProperties[];
	behaviorTreatmentTechniquesResponses: OptionProperties[];
	clientMoodResponses: OptionProperties[];
	howBarrierAffect: string;
	maladaptiveBehaviorTechniquesResponses: OptionProperties[];
	participantResponses: OptionProperties[];
	promptLevelsResponses: OptionProperties[];
	reinforcementResponses: OptionProperties[];
	sessionDescription: string;
	signedAt: string | null;
	skillDomainAddressedResponses: OptionProperties[];
	workedGoals: string;
	bcbaSignature: string | null;
	bcbaPrintedName: string | null;
	bcbaSignedAt: string | null;
}

export interface BehaviorTreatmentHardDataProperties {
	frequencyAndDurationOfMaladaptiveBehaviors?: string | null;
	hasRecordedMaladaptiveBehaviors: boolean;
	skillDomainNames: string[];
	reinforcementTitles: string[];
	workedGoals?: string | null;
}

export interface BehaviorTreatmentOptionsProperties {
	barrierToTreatmentOptions: OptionProperties[];
	behaviorTreatmentFuturePlanOptions: OptionProperties[];
	behaviorTreatmentParticipantOptions: ParticipantOptionsProperties[];
	behaviorTreatmentTechniqueOptions: OptionProperties[];
	clientMoodOptions: OptionProperties[];
	clientResponseToTreatmentOptions: OptionProperties[];
	levelOfMaladaptiveBehaviorOptions: OptionProperties[];
	maladaptiveBehaviorTechniqueOptions: OptionProperties[];
	promptLevelOptions: OptionProperties[];
	reinforcementOptions: OptionProperties[];
	skillDomainAddressedOptions: OptionProperties[];
}

interface DirectSupervisionDataProperties extends DataProperties {
	behaviorTechName: string;
	changesAndMeasurementsObserved: string;
	clientPerformance: string;
	dataEnteredCorrectly: boolean;
	descriptionOfSession: string;
	feedbackOther: string;
	implementationOfGoal: string;
	isReinforcerImplementationRight: boolean;
	promptedEffectively: boolean;
	responseToBehavioralFeedback: string;
	supervisedSessionId: string | null;
	supervisorId: string;
}

export interface UpdateDirectSupervisionProperties extends DirectSupervisionDataProperties, IdDataProperties {
	directSupervisionParticipantOther: string;
	feedbackResponses: string[];
	participantResponses: string[];
}

export interface DirectSupervisionOptionsProperties {
	directSupervisionParticipantOptions: ParticipantOptionsProperties[];
	feedbackOptions: OptionProperties[];
}

export interface DirectSupervisionProperties extends SoapNotesFullProperties, DirectSupervisionDataProperties {
	feedbackResponses: OptionProperties[];
	participantResponses: OptionProperties[];
}

interface ParentTrainingDataProperties extends DataProperties {
	antecedent: string;
	barriersToTreatment: string;
	behavior: string;
	behavioralMethodsUsed: string;
	consequence: string;
	goalsAddressed: string;
	parentFollowing: boolean;
	parentResponseToIntervention: string;
}

export interface UpdateParentTrainingProperties extends ParentTrainingDataProperties, IdDataProperties {
	directParentTrainingResponses: string[];
	parentTrainingParticipantOther: string;
	participantResponses: string[];
}

export interface ParentTrainingProperties extends SoapNotesFullProperties, ParentTrainingDataProperties {
	directParentTrainingResponses: OptionProperties[];
	participantResponses: OptionProperties[];
}

export interface ParentTrainingOptionsProperties {
	directParentTrainingOptions: OptionProperties[];
	parentTrainingParticipantOptions: ParticipantOptionsProperties[];
}

interface TreatmentPlanningDataProperties extends DataProperties {
	analysisOfGoals: string;
	coordinationAndMaterialsPrepped: string;
	descriptionOfSession: string;
	goalUpdates: string;
	masteredGoalsAndObjectives: string;
	newGoalsAndObjectives: string;
	treatmentPlanningParticipantOther: string;
	treatmentUpdateOther: string;
}

export interface UpdateTreatmentPlanningProperties extends TreatmentPlanningDataProperties, IdDataProperties {
	participantResponses: string[];
	treatmentUpdateResponses: string[];
}

export interface TreatmentPlanningProperties extends SoapNotesFullProperties, TreatmentPlanningDataProperties {
	participantResponses: OptionProperties[];
	treatmentUpdateResponses: OptionProperties[];
}

export interface TreatmentPlanningOptionsProperties {
	treatmentPlanningParticipantOptions: ParticipantOptionsProperties[];
	treatmentUpdateOptions: OptionProperties[];
}

export interface UpdateMeetAndGreetProperties extends DataProperties, IdDataProperties {
	barrierToTreatmentOther: string;
	barrierToTreatmentResponses: string[];
	meetAndGreetParticipantOther: string;
	participantResponses: string[];
}

export interface MeetAndGreetOptionsProperties {
	meetAndGreetBarrierToTreatmentOptions: OptionProperties[];
	meetAndGreetParticipantOptions: ParticipantOptionsProperties[];
}

export interface MeetAndGreetProperties extends SoapNotesFullProperties, DataProperties {
	barrierToTreatmentResponses: OptionProperties[];
	participantResponses: OptionProperties[];
}

interface DiscretionarySessionDataProperties {
	date: string;
	endTime: string;
	forcedOperation: boolean;
	sessionId: string;
	sessionSummary: string;
	signature: string;
	startTime: string;
	submit: boolean;
}

export interface DiscretionarySessionProperties extends SoapNotesFullProperties, DiscretionarySessionDataProperties {}

export interface UpdateDiscretionarySessionProperties extends DiscretionarySessionDataProperties, IdDataProperties {}

interface IntroCallDataProperties {
	attendees: string;
	callTookPlace: boolean;
	date: string;
	endTime: string;
	note: string;
	sessionId: string;
	startTime: string;
}

export interface IntroCallProperties extends SoapNotesFullProperties, IntroCallDataProperties {}

export interface UpdateIntroCallProperties extends IntroCallDataProperties, IdDataProperties {}
